import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const SiteContext = createContext(null);

export const SiteProvider = ({ children }) => {
    const [settings, setSettings] = useState(null);
    const [theme, setTheme] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const [s, t] = await Promise.all([api.get("/site-settings"), api.get("/theme")]);
            setSettings(s.data); setTheme(t.data);
        } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Apply theme CSS vars at the document root
    useEffect(() => {
        if (!theme) return;
        const r = document.documentElement;
        if (theme.accent_hsl) r.style.setProperty("--accent", theme.accent_hsl);
        if (theme.bg_color) r.style.setProperty("--theme-bg", theme.bg_color);
        if (theme.text_color) r.style.setProperty("--theme-fg", theme.text_color);
        if (theme.button_bg) r.style.setProperty("--theme-btn-bg", theme.button_bg);
        if (theme.button_text) r.style.setProperty("--theme-btn-fg", theme.button_text);
        if (theme.button_radius !== undefined) r.style.setProperty("--btn-radius", `${theme.button_radius}px`);
        if (theme.heading_font) r.style.setProperty("--font-display", `"${theme.heading_font}", sans-serif`);
        if (theme.body_font) r.style.setProperty("--font-body", `"${theme.body_font}", sans-serif`);
        if (theme.dark_mode) {
            document.body.classList.add("theme-dark");
            document.body.style.background = theme.bg_color || "#0A0A0A";
            document.body.style.color = theme.text_color || "#FAFAFA";
        } else {
            document.body.classList.remove("theme-dark");
            document.body.style.background = theme.bg_color || "";
            document.body.style.color = theme.text_color || "";
        }
    }, [theme]);

    // Favicon + SEO
    useEffect(() => {
        if (!settings) return;
        if (settings.favicon_url) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
            link.href = settings.favicon_url;
        }
        document.title = settings.seo_title || `${settings.store_name} — Streetwear on demand`;
        let desc = document.querySelector("meta[name='description']");
        if (!desc) { desc = document.createElement("meta"); desc.name = "description"; document.head.appendChild(desc); }
        desc.content = settings.seo_description || "";
    }, [settings]);

    return (
        <SiteContext.Provider value={{ settings, theme, refresh, setSettingsLocal: setSettings, setThemeLocal: setTheme }}>
            {children}
        </SiteContext.Provider>
    );
};

export const useSite = () => useContext(SiteContext) || { settings: null, theme: null };
